import React from "react";

import { useSelector } from "react-redux";
import { useSnapshot } from "valtio";

import type { DefinedColor } from "@busbud/design-system-components/dist/Button";
import Divider from "@busbud/design-system-components/dist/Divider";
import ExternalLinkMD from "@busbud/design-system-components/dist/Icons/ExternalLink/MD";
import LongArrowRightMD from "@busbud/design-system-components/dist/Icons/LongArrowRight/MD";
import Label from "@busbud/design-system-components/dist/Label";
import Stack from "@busbud/design-system-components/dist/Stack";
import { Button, type ButtonProps } from "@busbud/horizon";

import { patchSearchAdultWithSelectedSeats } from "@app/components/navigation-elements/navigation-wrapper-layout";
import { useAppContext } from "@app/helpers/hooks";
import { optionsDisplay } from "@app/helpers/options-display";
import { useNavigation } from "@app/modules/results/hooks/use-navigation";
import { getSearch } from "@app/modules/results/selectors/state-accessors";
import { useSeatSelectionFirst } from "@app/modules/seat-selection-first/hooks/use-seat-selection-first";
import { valtio_store } from "@app/modules/seat-selection-first/store";
import { showPassengerSelector } from "@app/modules/ticket-options/helpers";
import { useSegmentsIdWithSeatSelection } from "@app/modules/ticket-options/hooks/use-segments-id-with-seat-selection";
import { PassengerSelectorMobile } from "@app/modules/ticket-options/passenger-selector/passenger-selector-mobile";
import { usePassengerStore } from "@app/modules/ticket-options/passenger-selector/passenger-store";
import type { TripOption, TripOptionsMatrix } from "@app/types/results-types";

import { BottomSectionSkeleton } from "./bottom-section-skeleton";
import {
  getLocalizedPrice,
  getLocalizedPriceWithoutSymbol
} from "../../../localized-price";

interface Props {
  is_external?: boolean;
  selected_trip?: TripOption;
  is_vehicle_available: boolean | undefined;
  is_vehicle_loading: boolean;
  trip_options_matrix: TripOptionsMatrix;
}

export const BottomSection: React.FC<Props> = ({
  is_external,
  selected_trip,
  is_vehicle_loading,
  is_vehicle_available,
  trip_options_matrix
}) => {
  const { translator, locale, intlProvider } = useAppContext();
  const search = useSelector(getSearch);
  const { selectTrip, is_roundtrip, cart_id } = useNavigation();

  const { segments_id_with_seat_selection } = useSegmentsIdWithSeatSelection(
    selected_trip?.trip_id
  );

  const first_segment_id_with_seat_selection: string | undefined =
    segments_id_with_seat_selection[0]?.id;

  const { is_seat_selection_first_enabled } = useSeatSelectionFirst();
  const { selected_seats } = useSnapshot(valtio_store);
  const { selected_seats_count } = usePassengerStore();

  if (!selected_trip) {
    return <BottomSectionSkeleton />;
  }

  const localized_price = getLocalizedPrice(
    {
      price_in_cents: is_seat_selection_first_enabled
        ? selected_trip.price_in_cents * selected_seats_count
        : selected_trip.price_in_cents
    },
    {
      locale,
      intlProvider
    }
  );

  const localized_price_without_symbol =
    getLocalizedPriceWithoutSymbol(localized_price);

  const price_meta_props = localized_price_without_symbol
    ? {
        itemProp: "price",
        content: localized_price_without_symbol
      }
    : {};

  const deprecated_button_props =
    is_external && !is_roundtrip
      ? {
          children: translator.t("!results.select-referral-departure"),
          color: "secondary" as DefinedColor,
          endIcon: <ExternalLinkMD />
        }
      : {
          children: translator.t(
            "!error.SelectedSeatsNotAvailable.button.continue"
          ),
          color: "accent" as DefinedColor,
          endIcon: <LongArrowRightMD />
        };

  const button_props: React.PropsWithChildren<ButtonProps> =
    is_external && !is_roundtrip
      ? {
          children: deprecated_button_props.children,
          iconEnd: <ExternalLinkMD />
        }
      : {
          children: deprecated_button_props.children,
          appearance: "primary",
          iconEnd: <LongArrowRightMD />
        };

  const search_with_selected_seats_count = patchSearchAdultWithSelectedSeats(
    search,
    selected_seats_count
  );
  const selected_trip_information = optionsDisplay(
    translator,
    search_with_selected_seats_count,
    selected_trip,
    trip_options_matrix
  );

  const show_passenger_selector = showPassengerSelector({
    is_external,
    is_seat_selection_first_enabled,
    is_vehicle_available,
    is_roundtrip,
    cart_id
  });

  const disabled =
    (!!is_seat_selection_first_enabled && is_vehicle_loading) ||
    !selected_trip.price_in_cents;

  return (
    <Stack direction="column" spacing={2}>
      {!!show_passenger_selector && (
        <>
          <PassengerSelectorMobile />
          <Divider />
        </>
      )}
      <Stack
        justifyContent="space-between"
        itemProp="offers"
        itemScope
        itemType="https://schema.org/Offer"
      >
        <Stack direction="column" spacing={0.5}>
          <Label size="xl" fontWeight="bold">
            {translator.t("!checkout.summary.fees-taxes.total")}
          </Label>
          <Label size="xs" fontWeight="bold" color="text.tertiary.opaque">
            {selected_trip_information}
          </Label>
        </Stack>
        <Label
          size="xl"
          fontWeight="bold"
          data-testid="trip-displayed-price"
          data-cy="trip-displayed-price"
          {...price_meta_props}
        >
          {localized_price}
        </Label>
        <meta
          data-testid="trip-displayed-price-currency"
          itemProp="priceCurrency"
          content={intlProvider.currency}
        />
      </Stack>
      <Button
        data-cy="confirm-departure-selection"
        isDisabled={disabled}
        className="w-full"
        onClick={() => {
          selectTrip(
            selected_trip,
            is_external,
            is_vehicle_available,
            selected_seats,
            first_segment_id_with_seat_selection
          );
        }}
        size="xl"
        {...button_props}
      />
    </Stack>
  );
};
